import { Tooltip, TooltipArrow } from "@norkron/web-tooltip";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
// comment-visibility §6A — the card's interactive scope-indicator pill. Drives
// the §3.6 PATCH /api/comments/:cid/visibility route via `onChange`.
import type {
  CommentVisibility,
  MentionRef,
  WorkspaceMember,
} from "../../api/types";
import { Building2, Globe, User, Users, X } from "../../compose/Icon";
import { initials } from "../../lib/format";

/**
 * The card's interactive scope indicator (comment-visibility §6A) — an
 * icon-only colored pill in Row-1 of the review-note card that (a) animates a
 * grid-width reveal of its label on hover / keyboard focus / while its popup is
 * open, and (b) opens a `role="dialog"` popup (up-caret pointing at the pill)
 * carrying a radiogroup of the four scopes + an inline shared-audience editor.
 * It drives the §3.6 `PATCH /api/comments/:cid/visibility` route via `onChange`.
 * ROOT cards only — replies inherit the root's scope (§2.4), so they render no
 * pill.
 *
 * Color is never the only signal (WCAG 1.4.1): every scope also carries a
 * distinct glyph AND a text label (revealed on expand / always in `aria-label`).
 */

/** Per-scope glyph, label and one-line description (§6A.1 / §6A.3). */
type ScopeMeta = {
  scope: CommentVisibility;
  label: string;
  desc: string;
  Glyph: typeof Globe;
};

const SCOPES: ScopeMeta[] = [
  { scope: "public", label: "Public", desc: "Everyone on this document", Glyph: Globe },
  { scope: "internal", label: "Internal", desc: "Cerebr8 team only", Glyph: Building2 },
  { scope: "shared", label: "Shared", desc: "Only the people you pick", Glyph: Users },
  { scope: "private", label: "Private", desc: "Only you", Glyph: User },
];

const META: Record<CommentVisibility, ScopeMeta> = {
  public: SCOPES[0],
  internal: SCOPES[1],
  shared: SCOPES[2],
  private: SCOPES[3],
};

/** The pill's at-rest label (revealed on expand). Shared shows its count. */
function pillLabel(scope: CommentVisibility, count: number): string {
  return scope === "shared" ? `Shared · ${count}` : META[scope].label;
}

/**
 * What the hover/focus tooltip says for a scope that has no audience list.
 *
 * Only `shared` names actual people; the other three scopes are RULES, so the
 * tooltip states the rule in one plain sentence a client can act on — never an
 * empty "Shared with" row. Written for the person reading the note, not for an
 * engineer: no scope jargon, no field names, no counts.
 */
const SCOPE_TIP: Record<CommentVisibility, string> = {
  public: "Everyone with access to this document can see this note.",
  internal: "Only the Cerebr8 team can see this note.",
  shared: "This note isn't shared with anyone yet.",
  private: "Only you can see this note.",
};

/** How many audience names the tooltip lists before "+k more". */
const SHARED_WITH_SHOWN = 8;

/**
 * The pill's hover/focus tooltip body (positioned + flipped + arrow-drawn by
 * `@norkron/web-tooltip`; this is only the content).
 *
 * `shared` shows WHO — the initials avatars + names re-homed from the old
 * always-on `comment-card__sharedwith` card row, keeping that row's exact class
 * names so the type, avatars and shape are unchanged. Every other scope shows
 * its one-line rule from {@link SCOPE_TIP} in the SAME frame, tinted with its
 * own scope colour so the tooltip reads as an extension of the pill it came
 * from rather than a generic popup.
 */
function VisibilityTip({
  commentId,
  visibility,
  sharedWith,
}: {
  commentId: number;
  visibility: CommentVisibility;
  sharedWith: MentionRef[];
}) {
  const meta = META[visibility];
  const Glyph = meta.Glyph;
  const shown = sharedWith.slice(0, SHARED_WITH_SHOWN);
  const extra = sharedWith.length - shown.length;
  // Only `shared` has a real audience — and only once someone is actually in it.
  const hasAudience = visibility === "shared" && sharedWith.length > 0;
  return (
    <>
      <span className="comment-card__sharedwith-glyph" aria-hidden="true">
        <Glyph size={12} strokeWidth={1.75} />
      </span>
      <span className="comment-card__sharedwith-label">
        {hasAudience ? "Shared with" : meta.label}
      </span>
      {hasAudience ? (
        <span className="comment-card__sharedwith-people">
          {shown.map((p) => (
            <span
              key={p.user_id}
              className="comment-card__sharedwith-person"
              data-testid={`vis-tip-name-${commentId}-${p.user_id}`}
            >
              <span className="comment-card__sharedwith-avatar" aria-hidden="true">
                {initials(p.name)}
              </span>
              <span className="comment-card__sharedwith-name">{p.name}</span>
            </span>
          ))}
          {extra > 0 ? (
            <span
              className="comment-card__sharedwith-more"
              data-testid={`vis-tip-more-${commentId}`}
            >
              +{extra} more
            </span>
          ) : null}
        </span>
      ) : (
        <span
          className="comment-card__vistip-desc"
          data-testid={`vis-tip-desc-${commentId}`}
        >
          {SCOPE_TIP[visibility]}
        </span>
      )}
      <TooltipArrow />
    </>
  );
}

export function CommentVisibilityPill({
  commentId,
  visibility,
  sharedWith = [],
  members,
  canEdit,
  busy,
  onChange,
}: {
  commentId: number;
  /** The persisted scope (drives `data-visibility`, the pill token + glyph). */
  visibility: CommentVisibility;
  /** The persisted audience (resolved `{user_id, name}`) — non-empty only when
   *  `visibility === "shared"`. Drives the "Shared · N" count + chip names. */
  sharedWith?: MentionRef[];
  /** Workspace members for the audience multi-select (same `listMembers`
   *  source the @-mention combobox uses — §6.2). */
  members: WorkspaceMember[];
  /** author-or-staff (§3.6): false → the popup is read-only (radios + confirm
   *  disabled, no add-people input, no chip-remove). The pill still OPENS so a
   *  reader can SEE the scope. */
  canEdit: boolean;
  busy?: boolean;
  /** Applies a scope change (§6A.7). `shared_with` is sent only for `shared`. */
  onChange: (next: {
    visibility: CommentVisibility;
    shared_with?: number[];
  }) => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  // The scope currently SELECTED in the popup (may differ from the persisted
  // `visibility` while the reader is mid-edit; only committed on pick/confirm).
  const [selected, setSelected] = useState<CommentVisibility>(visibility);
  // The audience being edited (resolved {user_id, name}); seeded from props.
  const [audience, setAudience] = useState<MentionRef[]>(sharedWith);
  const [query, setQuery] = useState("");
  // §6A bug-1: the candidate member list stays open while the add-people input
  // is focused (not only while a query is typed), so multiple people can be
  // picked in a row without the list collapsing after each pick.
  const [inputFocused, setInputFocused] = useState(false);
  // §6A bug-2: a per-grant PATCH is in flight — disable further picks/removes so
  // rapid clicks can't race overlapping audience writes (and clobber the
  // optimistic state when the re-fetch re-seeds).
  const [pending, setPending] = useState(false);
  const [caretX, setCaretX] = useState(16);

  const wrapRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const radioRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const Glyph = META[visibility].Glyph;
  const editingShared = selected === "shared";

  // Re-seed the editor ONLY on the open transition (closed → open), never on a
  // live prop change while the popup stays open — the local editor state
  // (selected / audience / query / inputFocused) is the source of truth during an
  // active edit. The parent commits each per-grant PATCH and then RE-FETCHES
  // (DocumentViewer / ArticleWorkbench `onVisibility` → `reloadComments`), so the
  // very first pick flips the `visibility` prop "public"→"shared" and hands back a
  // fresh `sharedWith`. Re-seeding on that mid-session re-render would reset
  // `inputFocused` (collapsing the candidate list) and clobber the query after the
  // FIRST pick — reducing the multi-select to one person per open (the P0 bug).
  // `open` alone re-runs it: a re-open reflects the freshly committed state.
  useEffect(() => {
    if (open) {
      setSelected(visibility);
      setAudience(sharedWith);
      setQuery("");
      setInputFocused(false);
    }
    // Intentionally keyed on `open` ONLY — see the comment above. `visibility` /
    // `sharedWith` MUST NOT re-seed while open, or multi-add breaks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Outside-click / Esc dismiss. Esc returns focus to the pill (§6A.5).
  //
  // The outside-click check runs in the CAPTURE phase (the `true` 3rd arg). This
  // is load-bearing for multi-add: picking a candidate removes that person's <li>
  // from the list (they're now in the audience, so excluded). React flushes that
  // removal synchronously during the pick's bubble-phase handler, so a BUBBLE-phase
  // document listener would see `e.target` already DETACHED — `wrapRef.contains(
  // detachedNode)` is false → it would mis-read the pick as an OUTSIDE click and
  // dismiss the whole popup after the FIRST person (the P0 bug). The capture phase
  // fires BEFORE the target's own handlers and before any React re-render, so the
  // clicked node is still in the tree and containment is evaluated correctly. (An
  // `act()`-wrapped unit test hides this because act defers the flush — only a real
  // browser exposes it, which is why the E2E is the authoritative guard here.)
  useEffect(() => {
    if (!open) return;
    const onDownCapture = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        pillRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDownCapture, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDownCapture, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // On open: point the caret at the pill's horizontal center (§6A.3) and move
  // focus into the current scope radio (§6A.5 — focus trap entry).
  useLayoutEffect(() => {
    if (!open) return;
    const pill = pillRef.current;
    if (pill) setCaretX(pill.offsetLeft + pill.offsetWidth / 2);
    const idx = SCOPES.findIndex((s) => s.scope === selected);
    radioRefs.current[idx < 0 ? 0 : idx]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => setOpen(false);

  const commit = (next: {
    visibility: CommentVisibility;
    shared_with?: number[];
  }) => {
    void onChange(next);
    setOpen(false);
    pillRef.current?.focus();
  };

  // Picking a non-shared scope commits immediately (§6A.3.2). Picking "shared"
  // reveals the audience editor and waits for a Confirm (needs ≥1 person).
  const pickScope = (scope: CommentVisibility) => {
    if (!canEdit) return;
    if (scope === "shared") {
      setSelected("shared");
      return;
    }
    commit({ visibility: scope });
  };

  // APG radiogroup roving: arrows move selection + focus among the 4 scopes.
  const onRadioKeyDown = (e: React.KeyboardEvent, idx: number) => {
    let next = idx;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") next = (idx + 1) % SCOPES.length;
    else if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = (idx - 1 + SCOPES.length) % SCOPES.length;
    else return;
    e.preventDefault();
    radioRefs.current[next]?.focus();
    pickScope(SCOPES[next].scope);
  };

  const busyNow = busy === true || pending;

  // §6A.3/§6A.7 — per-grant persistence: every audience mutation is committed
  // immediately (optimistic local update + a scope PATCH carrying the full
  // resolved set). There is no un-committed "staged" audience to silently lose
  // on outside-click / Esc / Done, and `onChange` re-fetches so the card's
  // count + names update live. Never sends an empty `shared` (the server 400s).
  const patchAudience = async (next: MentionRef[]) => {
    setAudience(next);
    setPending(true);
    try {
      await onChange({ visibility: "shared", shared_with: next.map((a) => a.user_id) });
    } finally {
      setPending(false);
    }
  };

  const addPerson = (m: WorkspaceMember) => {
    if (!canEdit || busyNow) return;
    if (audience.some((x) => x.user_id === m.id)) return;
    setQuery("");
    void patchAudience([...audience, { user_id: m.id, name: m.name }]);
  };
  const removePerson = (userId: number) => {
    if (!canEdit || busyNow) return;
    const next = audience.filter((x) => x.user_id !== userId);
    // Disable-last (§6A): a shared note keeps ≥1 person. Removing the SOLE
    // remaining person is blocked here (the button is disabled too) — an empty
    // `shared` PATCH is a 400 by design; pick another scope to un-share instead.
    if (next.length === 0) return;
    void patchAudience(next);
  };

  const q = query.trim().toLowerCase();
  const candidates = members.filter(
    (m) =>
      !audience.some((x) => x.user_id === m.id) &&
      (q === "" || m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)),
  );

  const scopeLabel = pillLabel(visibility, sharedWith.length);

  return (
    <div className="comment-card__vispill-wrap" ref={wrapRef}>
      {/* Positioning, up/down edge-flip, the arrow, hover+FOCUS opening,
          `aria-describedby` wiring and Escape/outside dismissal all come from
          `@norkron/web-tooltip` (its `useTooltip` delegates dismissal to
          `@norkron/web-dismiss`) — this file hand-rolls none of it. `axis` is
          pinned vertical so the tip is BELOW the pill normally and ABOVE it when
          the card sits near the viewport bottom, never beside it. Suppressed
          while the scope DIALOG is open: a tooltip layered over the popup it
          belongs to is noise, and it would fight the dialog for Escape. */}
      <Tooltip
        axis="vertical"
        side="bottom"
        disabled={open || busy === true}
        disableHoverableContent
        // TIMING — the pill is an ANIMATING trigger (it grows a label track
        // over 220ms), so the tooltip must never measure it mid-reflow.
        //
        // `skipDelay={false}` is the load-bearing one: without it the
        // provider's warm-group bypass opens this tooltip with ZERO delay as
        // soon as any sibling tooltip in the card bar (Resolve, ⋯, the
        // deep-link button — all web-controls tooltips) has been open within
        // `skipDelayDuration`. That instant open anchors the tip to the pill's
        // COLLAPSED rect, and the tip then visibly jumps ~20px right as the
        // pill unfurls. Opting out means `openDelay` is always honoured.
        //
        // `openDelay` 300ms > the pill's 220ms transition, so the single
        // measurement always lands on a settled rect.
        //
        // `exitDelay` keeps the tip mounted and FROZEN in place for 160ms
        // after close — the lib tears its reposition listeners down at close,
        // so the tip fades out (`[data-state="closed"]`) exactly where it was
        // instead of chasing the pill as the pill wraps back. Reduced motion
        // is honoured by the lib's own stylesheet (the fade is dropped).
        //
        // `closeDelay={0}` makes that freeze immediate. The default 100ms
        // grace exists so a pointer can travel INTO hoverable content — this
        // tip is `disableHoverableContent`, so the grace buys nothing and
        // costs everything: measured live, the tip drifted 17px left during
        // those 100ms as the pill wrapped back underneath it, and only then
        // froze. Closing on leave means the last position it holds is the
        // position it was read at.
        skipDelay={false}
        openDelay={300}
        closeDelay={0}
        exitDelay={160}
        testId={`vis-tip-${commentId}`}
        contentClassName={`nk-tooltip-content comment-card__vistip comment-card__vistip--${visibility}`}
        content={
          <VisibilityTip
            commentId={commentId}
            visibility={visibility}
            sharedWith={sharedWith}
          />
        }
        trigger={
          <button
            type="button"
            ref={pillRef}
            className="comment-card__vispill"
            data-testid={`comment-visibility-pill-${commentId}`}
            data-visibility={visibility}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={`Visibility: ${scopeLabel}.${canEdit ? " Change" : " View"} who can see this note`}
            disabled={busy}
            onClick={() => setOpen((o) => !o)}
          >
            <span className="comment-card__vispill-icon" aria-hidden="true">
              <Glyph size={13} strokeWidth={1.75} />
            </span>
            <span
              className="comment-card__vispill-label"
              data-testid={`comment-visibility-label-${commentId}`}
            >
              {scopeLabel}
            </span>
          </button>
        }
      />

      {open ? (
        <div
          className="comment-card__vispop"
          data-testid={`comment-visibility-popup-${commentId}`}
          role="dialog"
          aria-label="Note visibility"
          ref={popupRef}
          style={{ "--caret-x": `${caretX}px` } as React.CSSProperties}
        >
          <div className="comment-card__vispop-caret" aria-hidden="true" />
          <div className="comment-card__vispop-header">Who can see this note</div>

          <div className="vis-scope-group" role="radiogroup" aria-label="Who can see this note">
            {SCOPES.map((s, i) => (
              <button
                key={s.scope}
                type="button"
                role="radio"
                ref={(el) => {
                  radioRefs.current[i] = el;
                }}
                className="vis-scope-opt"
                data-testid={`vis-scope-opt-${s.scope}-${commentId}`}
                data-scope={s.scope}
                aria-checked={selected === s.scope}
                tabIndex={selected === s.scope ? 0 : -1}
                disabled={!canEdit || busy}
                onClick={() => pickScope(s.scope)}
                onKeyDown={(e) => onRadioKeyDown(e, i)}
              >
                <span className="vis-scope-opt__icon" aria-hidden="true">
                  <s.Glyph size={15} strokeWidth={1.75} />
                </span>
                <span className="vis-scope-opt__text">
                  <span className="vis-scope-opt__label">{s.label}</span>
                  <span className="vis-scope-opt__desc">{s.desc}</span>
                </span>
              </button>
            ))}
          </div>

          {editingShared ? (
            <div className="vis-audience" data-testid={`vis-audience-picker-${commentId}`}>
              {audience.length > 0 ? (
                <div className="vis-audience-chips">
                  {audience.map((a) => (
                    <span
                      key={a.user_id}
                      className="vis-audience-chip"
                      data-testid={`vis-audience-chip-${commentId}-${a.user_id}`}
                    >
                      <span className="vis-audience-chip__name">{a.name}</span>
                      {canEdit ? (
                        <button
                          type="button"
                          className="vis-audience-chip__remove"
                          data-testid={`vis-audience-chip-remove-${commentId}-${a.user_id}`}
                          aria-label={`Remove ${a.name}`}
                          // Disable-last: never let the audience drop to empty
                          // (an empty `shared` PATCH 400s). Un-share via a scope.
                          disabled={busyNow || audience.length <= 1}
                          title={
                            audience.length <= 1
                              ? "A shared note keeps at least one person — pick another scope to un-share"
                              : `Remove ${a.name}`
                          }
                          onClick={() => removePerson(a.user_id)}
                        >
                          <X size={11} strokeWidth={2} />
                        </button>
                      ) : null}
                    </span>
                  ))}
                </div>
              ) : null}

              {canEdit ? (
                <>
                  <input
                    className="vis-audience-input"
                    type="text"
                    aria-label="Add people"
                    placeholder="Add people…"
                    value={query}
                    // Keep the list OPEN while the input is focused so people can
                    // be picked one after another (§6A bug-1). onMouseDown +
                    // preventDefault on each option keeps focus on the input, so
                    // a pick does not blur → the list stays up for the next pick.
                    onFocus={() => setInputFocused(true)}
                    onBlur={() => setInputFocused(false)}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  {inputFocused || q !== "" ? (
                    candidates.length > 0 ? (
                      <ul className="vis-audience-list" role="listbox" aria-label="Add people">
                        {candidates.map((m) => (
                          <li key={m.id} role="none">
                            <button
                              type="button"
                              role="option"
                              aria-selected={false}
                              className="vis-audience-item"
                              data-testid="vis-audience-item"
                              data-user-id={m.id}
                              disabled={busyNow}
                              // onMouseDown (not onClick) so the pick fires
                              // before the input blur — same as MentionCombobox.
                              onMouseDown={(e) => {
                                e.preventDefault();
                                addPerson(m);
                              }}
                            >
                              <span className="vis-audience-item__name">{m.name}</span>
                              <span className="vis-audience-item__email">{m.email}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="vis-audience-empty">
                        {q !== "" ? "No matching members" : "Everyone's already added"}
                      </div>
                    )
                  ) : null}
                </>
              ) : null}

              <div className="comment-card__vispop-footer">
                {canEdit ? (
                  <>
                    {audience.length === 0 ? (
                      <span className="comment-card__vispop-hint">
                        Pick at least one person to share with
                      </span>
                    ) : (
                      <span className="comment-card__vispop-hint">
                        Saved as you pick — add more or pick another scope to un-share
                      </span>
                    )}
                    <span className="comment-card__vispop-spacer" />
                    {/* Everything is persisted the moment it's picked (§6A bug-2),
                        so closing never loses a selection — this just dismisses. */}
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      data-testid={`vis-done-${commentId}`}
                      disabled={busyNow}
                      // preventDefault on mousedown so clicking Done does NOT blur
                      // the still-focused add-people input. Without this the blur
                      // collapses the candidate list (`inputFocused`→false), the
                      // popup reflows, and Done shifts out from under the pointer
                      // between mousedown and mouseup — so no `click` ever fires and
                      // the popup won't dismiss. Same onMouseDown-before-blur guard
                      // the candidate items use. onClick still runs (pointer +
                      // keyboard) once the reflow is prevented.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={close}
                    >
                      Done
                    </button>
                  </>
                ) : (
                  <span className="comment-card__vispop-hint">
                    Only the author or a Cerebr8 teammate can change this
                  </span>
                )}
              </div>
            </div>
          ) : !canEdit ? (
            <div className="comment-card__vispop-footer">
              <span className="comment-card__vispop-hint">
                Only the author or a Cerebr8 teammate can change this
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
